import OvertimeChart from '@components/ProjectOverview/Overview/OvertimeChart';
import SummaryChart from '@components/ProjectOverview/Overview/SummaryChart';
import { useLocalization } from '@lib/localization';
import { inspectionNoticeType } from '@lib/spproject/inspectionStatus';
import { Chip, Typography } from '@mui/material';
import type { IInspection } from '@store/api/inspectionEndpoints';
import React, { useMemo } from 'react';
import { ChipContainer, GridContainer, GridItem, StyledPaper } from './Overview.styled';
import useNoticeColors from '@store/hooks/useNoticeColors';

interface Stats {
  total: number;
  ok: number;
  warning: number;
  error: number;
  notChecked: number;
}

interface OverviewTabProps {
  inspections: IInspection[];
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ inspections }) => {
  const { t } = useLocalization();
  const noticeColors = useNoticeColors();

  const stats: Stats = useMemo(() => {
    if (!inspections || inspections.length === 0) {
      return { total: 0, ok: 0, warning: 0, error: 0, notChecked: 0 };
    }

    let ok = 0;
    let warning = 0;
    let error = 0;
    let notChecked = 0;

    inspections.forEach((inspection) => {
      switch (inspectionNoticeType(inspection.pointOfInterests)) {
        case 'Error':
          error++;
          break;
        case 'Warning':
          warning++;
          break;
        case 'Ok':
          ok++;
          break;
        default:
          notChecked++;
          break;
      }
    });

    return { total: inspections.length, ok, warning, error, notChecked };
  }, [inspections]);

  return (
    <>
      <GridContainer>
        <GridItem size={{ xs: 6, md: 3 }}>
          <StyledPaper>
            <Typography variant="subtitle2" color="text.secondary">
              {t('Project_TotalInspections')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 2, mb: 1 }}>
              {stats.total}
            </Typography>
          </StyledPaper>
        </GridItem>
        <GridItem size={{ xs: 6, md: 3 }}>
          <StyledPaper>
            <Typography variant="subtitle2" color="text.secondary">
              {t('Project_Overview_SuccessfulInspections')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 2, mb: 1, color: noticeColors.Ok }}>
              {stats.ok}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Project_Overview_SuccessfulInspections_Subtitle', [stats.total > 0 ? Math.round((stats.ok / stats.total) * 100) : 0])}
            </Typography>
          </StyledPaper>
        </GridItem>
        <GridItem size={{ xs: 6, md: 3 }}>
          <StyledPaper>
            <Typography variant="subtitle2" color="text.secondary">
              {t('Project_Overview_UnsuccessfulInspections')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 2, mb: 1, color: noticeColors.Warning }}>
              {stats.error + stats.warning}
            </Typography>
            <ChipContainer>
              <Chip
                size="small"
                label={t('Common_Error_Count', { count: stats.error })}
                sx={{ bgcolor: 'rgba(241, 65, 108, 0.1)', color: noticeColors.Error }} />
              <Chip
                size="small"
                label={t('Common_Warning_Count', { count: stats.warning })}
                sx={{ bgcolor: 'rgba(255, 199, 0, 0.1)', color: noticeColors.Warning }} />
            </ChipContainer>
          </StyledPaper>
        </GridItem>
        <GridItem size={{ xs: 6, md: 3 }}>
          <StyledPaper>
            <Typography variant="subtitle2" color="text.secondary">
              {t('Project_Overview_NotChecked')}
            </Typography>
            <Typography variant="h3" sx={{ mt: 2, mb: 1, color: '#7E8299' }}>
              {stats.notChecked}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('Project_Overview_NotChecked_Subtitle', [stats.total > 0 ? Math.round((stats.notChecked / stats.total) * 100) : 0])}
            </Typography>
          </StyledPaper>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem size={{ xs: 12, md: 5 }}>
          <StyledPaper>
            <SummaryChart inspections={inspections} />
          </StyledPaper>
        </GridItem>
        <GridItem size={{ xs: 12, md: 7 }}>
          <StyledPaper>
            <OvertimeChart inspections={inspections} />
          </StyledPaper>
        </GridItem>
      </GridContainer>
    </>
  );
};